import { Badge, Card, Flex, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';

import type { Listing, Nft } from '../api/backend';
import { colorTextMuted, rarityColors } from '../const/colors';
import getGueioImage from '../content/get_gueio_image';
import pitGueioSrc from '../content/pitgueio.webp';

const { Text } = Typography;

const FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="240" height="240">' +
            '<rect width="100%" height="100%" fill="#232736"/>' +
            '<text x="50%" y="50%" font-size="72" text-anchor="middle" dominant-baseline="central">🦆</text></svg>'
    );

export const capitalizeString = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

const formatTokenPrice = (value: number): string => {
    if (!Number.isFinite(value)) return '—';
    if (value >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    return String(parseFloat(value.toPrecision(4)));
};

const formatUsd = (value: string | null): string | null => {
    if (value == null) return null;
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return null;
    return `$${parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

interface NftCardProps {
    nft: Nft;
    listing: Listing | null;
    collectionName: string;
    collectionPath: string;
    statsNames: string[];
    boostsNames: string[];
}

const TraitRows = ({ nft, names, title }: { nft: Nft; names: string[]; title: string }) => {
    if (!nft.traits || !names.length) return null;
    const rows = Object.keys(nft.traits).filter((trait) => names.includes(trait.toLowerCase()));
    if (!rows.length) return null;
    return (
        <div style={{ marginTop: 10 }}>
            <Text style={{ color: colorTextMuted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                {title}
            </Text>
            {rows.map((trait) => (
                <Flex key={trait} justify='space-between' gap='middle' style={{ fontSize: 13 }}>
                    <span style={{ color: colorTextMuted }}>{capitalizeString(trait)}</span>
                    <b>{nft.traits?.[trait]}</b>
                </Flex>
            ))}
        </div>
    );
};

const NftCard = ({ nft, listing, collectionName, collectionPath, statsNames, boostsNames }: NftCardProps) => {
    let imageSrc = nft.image_url;
    if (collectionPath === 'gueio') imageSrc = getGueioImage(nft);
    if (collectionPath === 'pitgueio') imageSrc = pitGueioSrc;

    const rarity =
        nft.traits?.revealed === 'No'
            ? 'Unrevealed'
            : typeof nft.traits?.rarity === 'string'
              ? nft.traits.rarity
              : undefined;
    const usdPrice = listing ? formatUsd(listing.priceDollars) : null;

    const card = (
        <Card
            className='nft-card'
            size='small'
            cover={
                <img
                    className='nft-card-cover'
                    loading='lazy'
                    decoding='async'
                    alt={`${collectionName} #${nft.identifier}`}
                    src={imageSrc || FALLBACK_IMAGE}
                    onError={(event) => {
                        (event.target as HTMLImageElement).src = FALLBACK_IMAGE;
                    }}
                />
            }
        >
            <Text strong style={{ fontSize: 14 }}>
                {collectionName} #{nft.identifier}
            </Text>
            {listing && (
                <Flex align='baseline' gap={8} wrap style={{ marginTop: 4 }}>
                    <Text strong style={{ fontSize: 15 }}>
                        {formatTokenPrice(listing.priceToken)} {listing.price.current.currency}
                    </Text>
                    {usdPrice && (
                        <Text style={{ color: colorTextMuted, fontSize: 12 }}>{usdPrice}</Text>
                    )}
                </Flex>
            )}
            <TraitRows nft={nft} names={statsNames} title='Stats' />
            <TraitRows nft={nft} names={boostsNames} title='Boosts' />
            {nft.opensea_url && (
                <Flex justify='flex-end' style={{ marginTop: 10 }}>
                    <a target='_blank' rel='noopener noreferrer' href={nft.opensea_url} style={{ fontSize: 13 }}>
                        OpenSea <ExportOutlined />
                    </a>
                </Flex>
            )}
        </Card>
    );

    if (!rarity) return card;
    return (
        <Badge.Ribbon text={capitalizeString(rarity)} color={rarityColors[rarity.toLowerCase()] || '#595959'}>
            {card}
        </Badge.Ribbon>
    );
};

export default NftCard;
